import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const teamApi = createApi({
  reducerPath: "teamApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Teams", "TeamData", "TeamActivity"],
  keepUnusedDataFor: 300, // Keep cache for 5 minutes after unmount
  endpoints: (builder) => ({
    // Get user's teams
    getMyTeams: builder.query({
      query: () => ({
        url: "/teams/my-teams",
        method: "GET",
      }),
      providesTags: ["Teams"],
      transformResponse: (response) => response.data || [],
    }),

    // Get team data
    getTeamData: builder.query({
      query: (teamId) => ({
        url: `/teams/${teamId}`,
        method: "GET",
      }),
      providesTags: (result, error, teamId) => [
        { type: "TeamData", id: teamId },
      ],
      transformResponse: (response) => response.data,
    }),

    // Get team activity
    getTeamActivity: builder.query({
      query: (teamId) => ({
        url: `/teams/${teamId}/activity`,
        method: "GET",
      }),
      providesTags: (result, error, teamId) => [
        { type: "TeamActivity", id: teamId },
      ],
      transformResponse: (response) => response.data || [],
    }),

    // Create team
    createTeam: builder.mutation({
      query: (teamData) => ({
        url: "/teams/create",
        method: "POST",
        body: teamData,
      }),
      invalidatesTags: ["Teams"],
    }),

    // Join team
    joinTeam: builder.mutation({
      query: (data) => ({
        url: "/teams/join",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Teams"],
    }),

    // Leave team
    leaveTeam: builder.mutation({
      query: (teamId) => ({
        url: `/teams/${teamId}/leave`,
        method: "POST",
      }),
      invalidatesTags: ["Teams"],
    }),

    // Remove member
    removeMember: builder.mutation({
      query: ({ teamId, memberId }) => ({
        url: `/teams/${teamId}/members/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
        "Teams",
      ],
    }),

    // Delete team
    deleteTeam: builder.mutation({
      query: (teamId) => ({
        url: `/teams/${teamId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Teams"],
    }),

    // Add contact to team
    addTeamContact: builder.mutation({
      query: ({ teamId, ...contactData }) => ({
        url: `/teams/${teamId}/contacts`,
        method: "POST",
        body: contactData,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Add deal to team
    addTeamDeal: builder.mutation({
      query: ({ teamId, ...dealData }) => ({
        url: `/teams/${teamId}/deals`,
        method: "POST",
        body: dealData,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Add task to team
    addTeamTask: builder.mutation({
      query: ({ teamId, ...taskData }) => ({
        url: `/teams/${teamId}/tasks`,
        method: "POST",
        body: taskData,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Update task status
    updateTeamTaskStatus: builder.mutation({
      query: ({ teamId, taskId, status }) => ({
        url: `/teams/${teamId}/tasks/${taskId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Add note to team
    addTeamNote: builder.mutation({
      query: ({ teamId, ...noteData }) => ({
        url: `/teams/${teamId}/notes`,
        method: "POST",
        body: noteData,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Update team contact
    updateTeamContact: builder.mutation({
      query: ({ teamId, contactId, ...data }) => ({
        url: `/teams/${teamId}/contacts/${contactId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
      ],
    }),

    // Delete team contact
    deleteTeamContact: builder.mutation({
      query: ({ teamId, contactId }) => ({
        url: `/teams/${teamId}/contacts/${contactId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Update team deal
    updateTeamDeal: builder.mutation({
      query: ({ teamId, dealId, ...data }) => ({
        url: `/teams/${teamId}/deals/${dealId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
      ],
    }),

    // Delete team deal
    deleteTeamDeal: builder.mutation({
      query: ({ teamId, dealId }) => ({
        url: `/teams/${teamId}/deals/${dealId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Update team task (full edit)
    updateTeamTask: builder.mutation({
      query: ({ teamId, taskId, ...data }) => ({
        url: `/teams/${teamId}/tasks/${taskId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
      ],
    }),

    // Delete team task
    deleteTeamTask: builder.mutation({
      query: ({ teamId, taskId }) => ({
        url: `/teams/${teamId}/tasks/${taskId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Delete team note
    deleteTeamNote: builder.mutation({
      query: ({ teamId, noteId }) => ({
        url: `/teams/${teamId}/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        { type: "TeamActivity", id: teamId },
      ],
    }),

    // Update member role
    updateMemberRole: builder.mutation({
      query: ({ teamId, memberId, role }) => ({
        url: `/teams/${teamId}/members/${memberId}/role`,
        method: "PATCH",
        body: { role },
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: teamId },
        "Teams",
      ],
    }),

    // Lead Routing
    getLeadRouting: builder.query({
      query: (teamId) => ({ url: `/teams/${teamId}/lead-routing` }),
      providesTags: (result, error, teamId) => [{ type: "TeamData", id: `${teamId}-routing` }],
      transformResponse: (res) => res.data || { enabled: false, method: "round_robin" },
    }),
    updateLeadRouting: builder.mutation({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/lead-routing`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { teamId }) => [
        { type: "TeamData", id: `${teamId}-routing` },
      ],
    }),
  }),
});

export const {
  useGetMyTeamsQuery,
  useGetTeamDataQuery,
  useGetTeamActivityQuery,
  useCreateTeamMutation,
  useJoinTeamMutation,
  useLeaveTeamMutation,
  useRemoveMemberMutation,
  useDeleteTeamMutation,
  useAddTeamContactMutation,
  useAddTeamDealMutation,
  useAddTeamTaskMutation,
  useUpdateTeamTaskStatusMutation,
  useAddTeamNoteMutation,
  useUpdateTeamContactMutation,
  useDeleteTeamContactMutation,
  useUpdateTeamDealMutation,
  useDeleteTeamDealMutation,
  useUpdateTeamTaskMutation,
  useDeleteTeamTaskMutation,
  useDeleteTeamNoteMutation,
  useUpdateMemberRoleMutation,
  useGetLeadRoutingQuery,
  useUpdateLeadRoutingMutation,
} = teamApi;
